import api from "@services/axios/apiManager";

/**
 * 是否允許把註冊統編寫進表單欄位。
 * 不變式：發票區設定未載入（bootstrapLoaded=false）或未開啟（showInvoice=false，opt-in 預設關）
 * 一律不帶入；欄位已有值（草稿回填/使用者輸入）不覆蓋；沒有註冊統編不帶入。
 */
export const shouldApplyRegisteredTaxId = (params: {
  bootstrapLoaded: boolean;
  showInvoice: boolean;
  currentValue: string;
  registered: string;
}): boolean => {
  const { bootstrapLoaded, showInvoice, currentValue, registered } = params;
  return bootstrapLoaded && showInvoice && !currentValue && !!registered;
};

/**
 * 決定統編帶入的動作。統編輸入框只在「統編」類型被選取時顯示，
 * 因此只填數字、不選類型的話使用者看不到帶入結果。
 *
 * - "fill-and-select"：使用者尚未動過發票區（類型為預設 carrier 且載具/統編皆空）
 *   → 自動切到「統編」並填入
 * - "fill"：使用者已選「統編」但欄位空 → 只填入
 * - null：其他情況（含已有值、隱藏、未載入、無註冊統編）一律不動
 */
export type TaxIdPrefillAction = "fill" | "fill-and-select" | null;

export const decideTaxIdPrefill = (params: {
  bootstrapLoaded: boolean;
  showInvoice: boolean;
  currentType: string;
  currentCarrier: string;
  currentTax: string;
  registered: string;
}): TaxIdPrefillAction => {
  const { bootstrapLoaded, showInvoice, currentType, currentCarrier, currentTax, registered } = params;
  if (!bootstrapLoaded || !showInvoice || !registered || currentTax) return null;
  if (currentType === "tax_id") return "fill";
  // carrier / other 皆可能是「預設值、使用者尚未表態」：載具與統編皆空即視為 pristine。
  // 帶入只發生在掛載當下（使用者互動前），之後使用者仍可自由改回。
  const pristine = (currentType === "carrier" || currentType === "other" || !currentType) && !currentCarrier;
  return pristine ? "fill-and-select" : null;
};

/**
 * 取得目前身分（CIS）註冊時留存的統編。
 *
 * 來源：GET /app-api/customer（CustomerInSuppliersResDto），
 * 統編在 customer_info.tax_id_number。序列化後鍵名可能為
 * camelCase（customerInfo.taxIdNumber）或 snake_case，兩者都容錯。
 * 取不到（未登入、練習模式沙盒未攔截、無統編）一律回空字串，不拋錯。
 */
export const fetchCustomerTaxId = async (xContext?: string, jwtToken?: string): Promise<string> => {
  try {
    const headers: Record<string, string> = {};
    if (xContext) headers["X-Context"] = xContext;
    if (jwtToken) headers.Authorization = `Bearer ${jwtToken}`;
    const res = await api.get("/app-api/customer", {
      headers: Object.keys(headers).length ? headers : undefined,
    });
    const data = (res as any)?.data?.data ?? (res as any)?.data;
    const taxId =
      data?.customerInfo?.taxIdNumber ??
      data?.customer_info?.tax_id_number ??
      "";
    return typeof taxId === "string" ? taxId.trim() : "";
  } catch {
    return "";
  }
};
